// scheduling/components/pool/AssignmentAckPanel.jsx
// "You were given a job" acknowledgment (Trello #257).
//
// This is the piece that closes the hand-off. RPRS stops tracking a job the moment
// it's claimed, so if a staffer gets ASSIGNED work while they were sitting marked
// available, there has to be one unmistakable moment where they're told:
//   • which job it is
//   • that their availability was turned off as a result
//   • how to get to it in PE
//
// Shown on first open of the Job Pools page after an assignment, driven by unread
// `pool_assignment` notifications. Dismissing marks them read, which also clears
// the nav pulse — same mechanism as the bug-resolved and suggestion-updated pulses
// in AppShell.
//
// Self-claims never land here: you don't need telling about a thing you just did.

import { POOL_META } from '../../utils/poolEligibility';

const PE_JOB_URL = (i) => `https://bmss.pehosted.com/PE/Job/Job/${i}?panel=0`;

function parseMeta(n) {
  try { return n.Metadata ? JSON.parse(n.Metadata) : {}; } catch (_) { return {}; }
}

export default function AssignmentAckPanel({ notifications, onDismiss }) {
  if (!notifications || notifications.length === 0) return null;

  return (
    <div className="sched-pool-ack-backdrop">
      <div className="sched-pool-ack">
        <div className="sched-pool-ack-head">
          <span className="sched-pool-ack-icon">📥</span>
          <div>
            <div className="sched-pool-ack-title">
              {notifications.length === 1
                ? 'You’ve been assigned a job from a pool'
                : `You’ve been assigned ${notifications.length} jobs from the pools`}
            </div>
            <div className="sched-pool-ack-sub">
              Your availability has been turned off, since you now have work.
            </div>
          </div>
        </div>

        <div className="sched-pool-ack-list">
          {notifications.map(n => {
            const meta = parseMeta(n);
            const pool = meta.poolCode;
            return (
              <div key={n.NotifId} className="sched-pool-ack-item">
                <div className="sched-pool-ack-item-head">
                  <span className="sched-pool-ack-item-title">{n.Title}</span>
                  {pool && (
                    <span className="sched-pool-chip sched-pool-chip--sm" style={{ background: POOL_META[pool]?.color }}>
                      {POOL_META[pool]?.label || pool}
                    </span>
                  )}
                </div>
                {n.Body && <div className="sched-pool-ack-item-body">{n.Body}</div>}
                <div className="sched-pool-ack-item-meta">
                  {n.ActorName && <span>Assigned by {n.ActorName}</span>}
                  {n.RelatedJobId && meta.peJobIndex && (
                    <a href={PE_JOB_URL(meta.peJobIndex)} target="_blank" rel="noopener noreferrer">
                      Open in PE ↗
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="sched-pool-ack-note">
          Tracking continues in Practice Engine from here — this job won&rsquo;t appear
          on your RPRS schedule. You&rsquo;ll find it under <strong>My recent pool work</strong>.
        </div>

        <div className="sched-pool-actions">
          <button type="button" className="sched-mgr-modal-btn sched-pool-btn-confirm" onClick={onDismiss}>
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
